import { Route } from '@angular/router';
import { requireTenantPermission } from '@/app/core/auth/guards/tenant-permission.guard';
import { TENANT_CONTROLS } from '@/app/core/current-user/tenant-controls';
import { tenantNameResolver } from '@/app/features/tenant-hub/data-access/tenant-name.resolver';
import { hubTabRequirements } from '@/app/features/tenant-hub/tenant-hub-tabs';

/** Where the Entra link page sits under the hub route (UX pregled 5, D-UX5-18): the route below and `entraLinkPageUrl` read it. */
const ENTRA_LINK_PAGE_PATH = 'prijava/entra';

/**
 * The router commands of an Entra link's page in the hub, `/tenanti/:tenantId/prijava/entra/:linkId` — the ONE place the address is
 * spelled (review T-3): the Entra registry's row and name and its after-create navigation, the provider page's „Koristi“ links and
 * the „Pristup“ card all take it from here.
 */
export function entraLinkPageUrl(tenantId: string, linkId: string): string[] {
    return ['/tenanti', tenantId, ...ENTRA_LINK_PAGE_PATH.split('/'), linkId];
}

// The tenant hub (T1-06): open to an operator AND to a tenant's own administrator — every guard below reads
// :tenantId and asks the server capability in THAT tenant (requireTenantPermission), never the sign-in sheet.
// A bounded administrator reaches it by URL; the tab guards are built from HUB_TABS (tenant-hub-tabs.ts), the same
// list the tab bar reads, and a detail page carries its tab's guard.
export const TENANT_HUB_ROUTE: Route = {
    path: 'tenanti/:tenantId',
    canActivate: [requireTenantPermission(TENANT_CONTROLS.hub)],
    data: { breadcrumb: 'navigation.tenants', breadcrumbResolve: 'tenantName' },
    resolve: { tenantName: tenantNameResolver },
    loadComponent: () => import('@/app/features/tenant-hub/tenant-hub.component').then((c) => c.TenantHubComponent),
    children: [
        { path: '', redirectTo: 'pregled', pathMatch: 'full' },
        { path: 'pregled', data: { breadcrumb: 'navigation.overview' }, loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-overview-tab.component').then((c) => c.TenantOverviewTabComponent) },
        {
            path: 'korisnici',
            data: { breadcrumb: 'navigation.users' },
            canActivate: [requireTenantPermission(...hubTabRequirements('korisnici'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-users-tab.component').then((c) => c.TenantUsersTabComponent)
        },
        {
            path: 'korisnici/:userId',
            data: { breadcrumb: 'navigation.user' },
            canActivate: [requireTenantPermission(...hubTabRequirements('korisnici'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-user-detail.component').then((c) => c.TenantUserDetailComponent)
        },
        {
            path: 'aplikacije',
            data: { breadcrumb: 'navigation.applicationsAndLeases' },
            canActivate: [requireTenantPermission(...hubTabRequirements('aplikacije'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-leases-tab.component').then((c) => c.TenantLeasesTabComponent)
        },
        {
            path: 'aplikacije/:appId',
            data: { breadcrumb: 'navigation.lease' },
            canActivate: [requireTenantPermission(...hubTabRequirements('aplikacije'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-lease-detail.component').then((c) => c.TenantLeaseDetailComponent)
        },
        {
            path: 'api-klijenti',
            data: { breadcrumb: 'navigation.apiClients' },
            canActivate: [requireTenantPermission(...hubTabRequirements('api-klijenti'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-api-clients-tab.component').then((c) => c.TenantApiClientsTabComponent)
        },
        {
            path: 'api-klijenti/:clientId',
            data: { breadcrumb: 'navigation.apiClient' },
            canActivate: [requireTenantPermission(...hubTabRequirements('api-klijenti'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-api-client-detail.component').then((c) => c.TenantApiClientDetailComponent)
        },
        {
            path: 'obavestenja',
            data: { breadcrumb: 'navigation.announcements' },
            canActivate: [requireTenantPermission(...hubTabRequirements('obavestenja'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-announcements-tab.component').then((c) => c.TenantAnnouncementsTabComponent)
        },
        {
            path: 'permisije',
            data: { breadcrumb: 'navigation.permissions' },
            canActivate: [requireTenantPermission(...hubTabRequirements('permisije'))],
            loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-permissions-tab.component').then((c) => c.TenantPermissionsTabComponent)
        },
        // The Bezbednost tab is gone (UX pregled 1, D9): everything it held lives on Pregled. Old links and bookmarks land there.
        { path: 'bezbednost', redirectTo: 'pregled', pathMatch: 'full' },
        { path: 'prijava', data: { breadcrumb: 'navigation.signIn' }, loadComponent: () => import('@/app/features/tenant-hub/tabs/tenant-login-tab.component').then((c) => c.TenantLoginTabComponent) },
        // The Entra link's page (UX pregled 5, D-UX5-18): federation administration, so its guard is a global consumers flag
        // (`viewLoginProviders`) — not the Prijava tab's (none). Lazy children: the crumb's resolver stays out of the main bundle.
        {
            path: `${ENTRA_LINK_PAGE_PATH}/:linkId`,
            canActivate: [requireTenantPermission(TENANT_CONTROLS.viewLoginProviders)],
            loadChildren: () => import('@/app/features/tenant-hub/tabs/tenant-entra-link.routes').then((r) => r.ENTRA_LINK_ROUTES)
        }
    ]
};
